import { omit } from 'radash';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance, differenceInHours } from 'date-fns';
import { Loading } from '../../components/loading';
import { StateBadge } from '../../components/badge';
import { useJobs } from '../../state';
import { colors, styles } from '../../constants';
import { TableFromObject } from '../table';
import { ArgsTable } from './Args';

const border = '1px solid #ccc';

const count = (job) =>
  job.meta?.items?.total || job.results?.items?.length || job.meta?.hits?.total || job.results?.hits?.length;

const Preview = ({ job }) => {
  const prettyCrawl = () => job.params?.query || (job.params?.steps || []).join(', ') || job.params?.pattern;

  try {
    switch (job.method) {
      case 'visit':
        return job.params?.url || (job.params?.urls || []).join(', ');
      case 'crawl':
        return prettyCrawl();
      case 'extract':
        return new URL(job.params?.urls[0]).host + ' ' + JSON.stringify(job.params?.template);
      case 'scrape':
        return prettyCrawl() + ' ' + JSON.stringify(job.params?.template);
      case 'agent':
        return job.params?.prompt;
    }
  } catch {
    return '...';
  }
  if (job.name == 'crawl') {
  }
};

const CostIcon = ({ cost }) => {
  const [hover, setHover] = useState();

  if (!cost) {
    return null;
  }

  // const { ai, network, fetchfox, total } = cost;
  const { ai = 0, network = 0, fetchfox = 0, total = 0 } = cost;
  if (total <= 0) return null;

  const pct = (v) => (v / total) * 100;

  const aiPct = pct(ai);
  const netPct = pct(network);
  const ffPct = pct(fetchfox);

  const cutoff = 60;

  const errorColor = '#ee8f8b';
  // const aiColor = aiPct > cutoff ? errorColor : '#a5b4fc';
  // const netColor = netPct > cutoff ? errorColor : '#7fd8c8';
  const aiColor = '#a5b4fc';
  const netColor = '#7fd8c8';
  const ffColor = '#f3dc9c';

  const height = 16;

  const hoverNode = () => (
    <div
      style={{
        position: 'absolute',
        top: 40,
        right: 0,
        background: 'white',
        border,
        borderRadius: 2,
        width: 200,
        zIndex: 100,
      }}
    >
      <TableFromObject
        percent={40}
        cellStyle={{ fontSize: 12 }}
        obj={{
          AI: `\$${ai.toFixed(4)} (${aiPct.toFixed(1)}%)`,
          Network: `\$${network.toFixed(4)} (${netPct.toFixed(1)}%)`,
          FetchFox: `\$${fetchfox.toFixed(4)} (${ffPct.toFixed(1)}%)`,
          Total: `\$${total.toFixed(4)} (100%)`,
        }}
      />
    </div>
  );

  return (
    <>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: 4,
          overflow: 'hidden',
          background: '#e9e9e9',
          fontSize: 10,
          lineHeight: '18px',
          textAlign: 'center',
          color: '#111',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
          }}
        >
          <div style={{ width: `${aiPct}%`, background: aiColor }} />
          <div style={{ width: `${netPct}%`, background: netColor }} />
          <div style={{ width: `${ffPct}%`, background: ffColor }} />
        </div>

        <div
          style={{
            position: 'relative',
            pointerEvents: 'none',
            lineHeight: height + 'px',
          }}
        >
          ${total.toFixed(4)}
        </div>
      </div>

      {hover && hoverNode()}
    </>
  );
};

const JobRow = ({ job, narrow }) => {
  const [hover, setHover] = useState();

  const hoverNode = () => {
    const args = {
      ...job.params,
    };
    delete args.signalId;

    const r = job;
    let results;
    if (job.results?.items?.length) {
      results = {
        items: count(job),
        '1)': job.results.items[0],
        '2)': job.results.items[1],
        '3)': job.results.items[2],
      };
    } else if (job.results?.hits?.length) {
      results = {
        hits: job.results.hits.length,
        '': job.results.hits,
      };
    }

    const table = (obj, header, style) => (
      <div style={{ overflow: 'hidden' }}>
        <div
          style={{
            margin: '8px 8px 0 8px',
            fontWeight: 'bold',
            fontSize: 16,
            fontVariantCaps: 'small-caps',
            color: '#888',
          }}
        >
          {header.toLowerCase()}
        </div>
        <TableFromObject
          obj={omit(obj, ['plan'])}
          percent={25}
          style={{ borderRadius: 5, margin: 8, background: '#f3f3f3' }}
          headerStyle={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
          cellStyle={{
            padding: '2px 4px',
            borderRadius: 5,
            maxHeight: 160,
            overflow: 'hidden',
            gap: 5,
            background: '#f3f3f3',
            ...style,
          }}
        />
      </div>
    );

    return (
      <div
        style={{
          position: 'absolute',
          display: 'flex',
          top: 35,
          left: 140,
          border: '1px solid #aaa',
          borderRadius: 5,
          background: 'white',
          zIndex: 2,
          width: '100%',
          maxWidth: 800,
          maxHeight: 300,
          overflow: 'hidden',
          boxShadow: '4px 4px 8px rgba(0,0,0,.2)',
        }}
      >
        <div style={{ width: '50%' }}>{table(args, 'Arguments')}</div>
        <div style={{ width: '50%' }}>{table(results, 'Results', { maxHeight: 300 })}</div>
      </div>
    );
  };

  const gap = 10;
  const iconWidth = 80;
  const mainWidth = `calc(100% - ${iconWidth + gap}px)`;

  return (
    <Link
      to={`/${job.method == 'agent' ? 'agents' : 'jobs'}/${job.jobId}`}
      key={job.jobId}
      style={{ position: 'relative', color: colors.text }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap,
          fontSize: 14,
          padding: 5,
          borderTop: border,
          overflow: 'hidden',
          background: hover ? '#ddd' : '',
          width: '100%',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap,
            width: mainWidth,
          }}
        >
          <div style={{ width: 20 }}>
            <StateBadge state={job.state} />
          </div>
          <div style={{ width: 30, textAlign: 'right' }}>{count(job) != undefined && count(job)}</div>
          {narrow && <div style={{ width: 10 }}>{job.method.substring(0, 1)}</div>}
          {!narrow && <div style={{ width: 40 }}>{job.method}</div>}
          {narrow && (
            <div style={{ width: 24 }}>{Math.max(0, differenceInHours(new Date(), new Date(job.createdAt)))}h</div>
          )}
          {!narrow && (
            <div style={{ width: 160, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {formatDistance(new Date(job.createdAt), new Date(), { addSuffix: true, includeSeconds: true })}
            </div>
          )}
          <div style={{ flex: 1, ...styles.textOverflowStyle }}>
            <Preview job={job} />
          </div>
        </div>
        <div style={{ width: iconWidth }}>
          <CostIcon cost={job.metrics?.cost} />
        </div>
      </div>
      {hover && hoverNode()}
    </Link>
  );
};

export const JobsList = ({ limit, forApiKey, types, narrow }) => {
  const { loading, didInit, jobs } = useJobs({
    limit,
    forApiKey,
    types: types || ['extract', 'crawl', 'scrape', 'agent'],
  });

  const nodes = (jobs || []).map((job) => <JobRow narrow={narrow} job={job} />);

  let listNode;
  if (!loading) {
    if (nodes.length) {
      listNode = <div style={{ borderBottom: border }}>{nodes}</div>;
    } else {
      listNode = <div>No jobs found</div>;
    }
  }

  return (
    <div>
      {(!didInit || loading) && <Loading />}
      {didInit && listNode}
    </div>
  );
};
